/**
 * Logique pure de l'écran Simulateur — liste, filtres, carte.
 *
 * Aucune dépendance framework (pas de JSX, pas de React Query) : dérivation des badges et du
 * statut d'affichage, construction des points et tracés de carte. Types de la réponse API déduits
 * du backend (`~/lib/logistics-simulations-query`). Testé par `simulator-list.test.mjs`.
 */

import {
  LOGISTICS_VEHICLE_TYPE_LABELS,
  type LogisticsStoreMapCategory,
  type LogisticsVehicleType,
} from '@gozoki/api-types'
import { formatInteger, pluralWord } from '@gozoki/tools'
import type { FitMapOptions } from '@gozoki/ui/lib/map-bounds'
import type {
  SimulatorListItem,
  SimulatorListQuery,
  SimulatorMapPathPlace,
  SimulatorNetwork,
  SimulatorScreenStatus,
} from '~/lib/logistics-simulations-query'
import type { LogisticsSearch } from '~/routes/_authenticated/logistics/-search-schema'
import { routeTraceBodiesOf, type RouteTraceBody } from './logistics-route-trace'
import type { StoreMapPlace } from './store-status-clusters'

/**
 * Paramètres de `GET /logistics/simulations` pour les filtres de l'URL de la page Logistique :
 * l'onglet mis à part, chaque filtre rejoint son paramètre d'API.
 */
export const simulatorListQueryOf = (
  filters: Omit<LogisticsSearch, 'tab'>,
): SimulatorListQuery => ({
  centralKitchenId: filters.kitchen,
  departmentCode: filters.department,
  from: filters.from,
  to: filters.to,
  q: filters.q,
})

/**
 * Filtres du panneau « Tournées simulées » qu'efface « Réinitialiser » : cuisine centrale,
 * département et période. La recherche, à part, reste.
 */
export const SIMULATOR_LIST_FILTERS_RESET = {
  kitchen: undefined,
  department: undefined,
  from: undefined,
  to: undefined,
} satisfies Partial<LogisticsSearch>

/** Au moins un filtre du panneau actif (la période va par paire, cf. `logisticsSearchSchema`). */
export const hasSimulatorListFilters = (
  search: Pick<LogisticsSearch, 'kitchen' | 'department' | 'from'>,
): boolean =>
  search.kitchen !== undefined || search.department !== undefined || search.from !== undefined

/**
 * « 12 tournées sur 56 » : tournées des filtres (recherche comprise), puis toutes, sans filtre.
 * Total sans filtre inconnu (lecture en cours ou en erreur) : le premier nombre seul.
 */
export const filteredToursLabel = (filtered: number, unfiltered: number | undefined): string => {
  const tours = `${formatInteger(filtered)} ${pluralWord(filtered, 'tournée')}`
  return unfiltered === undefined ? tours : `${tours} sur ${formatInteger(unfiltered)}`
}

/** Catégorie d'affichage de l'icône de statut : coche verte / croix rouge / tiret gris. */
export type SimulatorStatusDisplay = 'valid' | 'invalid' | 'draft'

/**
 * `validated` et `feasible` sont les deux issues positives (une tournée validée est forcément née
 * d'un scénario faisable) ; `infeasible`/`incomplete`/`failed` sont les trois échecs ; `draft` et
 * `running` n'ont simplement pas encore de résultat.
 */
export const statusDisplayOf = (status: SimulatorScreenStatus): SimulatorStatusDisplay => {
  if (status === 'validated' || status === 'feasible') return 'valid'
  if (status === 'infeasible' || status === 'incomplete' || status === 'failed') return 'invalid'
  return 'draft'
}

export interface SimulatorListBadges {
  /** Types de véhicules du scénario actif, dans l'ordre du backend (VL, PL, SL) ; vide sans scénario actif. */
  vehicleTypes: LogisticsVehicleType[]
  departure: TourDeparture
  hubCount: number
  storeCount: number
  /** Codes départements distincts des boutiques de la tournée, triés ; vide si aucun connu. */
  departmentCodes: string[]
}

/** Atelier ou hub nommé, tel que l'affiche `PlaceBadge`. */
export interface NamedPlace {
  kind: 'kitchen' | 'hub'
  label: string
}

/** Point de départ d'une tournée : l'atelier, ou le hub d'un dernier km parti d'un hub (LU-196). */
export type TourDeparture = NamedPlace

/** Pastille de départ d'un lieu de scénario déjà nommé (`placeLabel`). */
export const tourDepartureOf = (
  place: Pick<SimulatorMapPathPlace, 'type'>,
  label: string,
): TourDeparture => ({
  kind: place.type === 'hub' ? 'hub' : 'kitchen',
  label,
})

/** Types d'un badge ou d'un export, sous leurs libellés courts (« VL, PL, SPL »). */
export const vehicleTypesLabel = (types: readonly LogisticsVehicleType[]): string =>
  types.map((type) => LOGISTICS_VEHICLE_TYPE_LABELS[type].short).join(', ')

/**
 * Badges d'une ligne : « types de véhicules · atelier › nb hubs (si > 0) › nb boutiques ·
 * départements ». Le composant décide de l'affichage (icônes, séparateurs) ; cette fonction ne
 * fait que dériver les valeurs. Types de véhicules : dédupliqués, SANS retri (l'ordre VL, PL, SL
 * du backend, du plus léger au plus lourd, n'est pas alphabétique). Compteurs : ceux du scénario
 * actif s'il existe (ce que la tournée dessert vraiment), sinon ceux du périmètre de la
 * simulation. Départements : toujours ceux du périmètre de la simulation (l'API n'expose pas la
 * liste des boutiques du scénario actif, seulement son compteur).
 */
export const badgesOf = (item: SimulatorListItem): SimulatorListBadges => ({
  vehicleTypes: item.activeScenario ? [...new Set(item.activeScenario.vehicleTypes)] : [],
  departure: item.departureHub
    ? { kind: 'hub', label: item.departureHub.name }
    : { kind: 'kitchen', label: item.kitchen.label },
  hubCount: item.activeScenario?.hubCount ?? item.hubs.length,
  // Boutiques en projet comprises (#941), comme le compteur du scénario actif.
  storeCount: item.activeScenario?.storeCount ?? item.stores.length + item.storeProjects.length,
  departmentCodes: departmentCodesOf(item),
})

/**
 * Codes départements distincts des boutiques d'une tournée, triés — cf. `badgesOf`. `null`
 * (boutique sans adresse rattachée à un département) est ignoré. Sans les boutiques en projet :
 * le filtre par département de l'API ne lit que les boutiques (`store_ids`).
 */
const departmentCodesOf = (item: SimulatorListItem): string[] => {
  const codes = new Set<string>()
  for (const store of item.stores) {
    if (store.departmentCode !== null) codes.add(store.departmentCode)
  }
  return [...codes].sort((a, b) => a.localeCompare(b, 'fr'))
}

export type SimulatorMapPointKind = 'kitchen' | 'hub' | 'store'

export interface SimulatorMapPoint {
  /** Clé stable et unique tous types confondus (ex. `kitchen-3`, `store-42`) : sert d'id GeoJSON. */
  id: string
  kind: SimulatorMapPointKind
  label: string
  latitude: number
  longitude: number
  /**
   * Catégorie d'une boutique ou d'un dossier du foncier (celle de son regroupement), toujours
   * renseignée par le backend ; `null` pour un atelier ou un hub.
   */
  mapCategory: LogisticsStoreMapCategory | null
}

export interface SimulatorMapDisplayFilters {
  showKitchens: boolean
  showHubs: boolean
  showStores: boolean
  /** Tracés des scénarios actifs. */
  showRoutes: boolean
}

/**
 * Tournées retenues par le filtre de statut de la carte (`null` = aucun filtre), selon le statut
 * que lit l'écran : celui de la simulation au simulateur, le statut prévu en gestion opérationnelle.
 */
export const visibleItemsOf = <TItem, TStatus>(
  items: TItem[],
  statuses: ReadonlySet<TStatus> | null,
  statusOf: (item: TItem) => TStatus,
): TItem[] => (statuses === null ? items : items.filter((item) => statuses.has(statusOf(item))))

export const DEFAULT_MAP_DISPLAY_FILTERS: SimulatorMapDisplayFilters = {
  showKitchens: true,
  showHubs: true,
  showStores: true,
  showRoutes: true,
}

/** Lieu d'un tracé qui n'existe plus (libellé `null` côté API) : il n'a plus de nom. */
export const REMOVED_PLACE_LABEL = 'Lieu supprimé'

/**
 * Type de lieu du contrat solveur → type de point des cartes (et de ses couleurs, `PLACE_COLORS`).
 * Une boutique en projet (#941) prend le marqueur d'une boutique : ces cartes n'ont pas encore le
 * sien (marqueur pointillé de la mini-carte de création), sa clé reste propre au projet.
 */
export const PATH_PLACE_KIND: Record<SimulatorMapPathPlace['type'], SimulatorMapPointKind> = {
  central_kitchen: 'kitchen',
  hub: 'hub',
  store: 'store',
  store_project: 'store',
}

const DISPLAY_FLAG_OF_KIND: Record<
  SimulatorMapPointKind,
  keyof Omit<SimulatorMapDisplayFilters, 'showRoutes'>
> = {
  kitchen: 'showKitchens',
  hub: 'showHubs',
  store: 'showStores',
}

/**
 * Point de carte d'un lieu, `null` s'il n'est pas géolocalisé : clé `<type>-<id>`, commune aux
 * cartes. `mapCategory` : catégorie d'une boutique, `null` pour un atelier ou un hub.
 */
export const mapPointOf = (
  kind: SimulatorMapPointKind,
  id: number,
  label: string,
  latitude: number | null,
  longitude: number | null,
  mapCategory: LogisticsStoreMapCategory | null,
): SimulatorMapPoint | null =>
  latitude === null || longitude === null
    ? null
    : { id: `${kind}-${id}`, kind, label, latitude, longitude, mapCategory }

/**
 * Boutique (ou dossier du foncier) des cartes : sa catégorie est toujours renseignée par le
 * backend ; `null` est celle d'un atelier ou d'un hub.
 */
export const isStorePoint = (
  point: SimulatorMapPoint,
): point is SimulatorMapPoint & StoreMapPlace =>
  point.kind === 'store' && point.mapCategory !== null

/** Atelier ou hub des cartes, posé en épingle ronde (`LogisticsPlacePin`). */
export type PlacePinPoint = SimulatorMapPoint & { kind: Exclude<SimulatorMapPointKind, 'store'> }

/** Ce que la carte lit d'une boutique ou d'un dossier du foncier, d'une tournée ou du réseau. */
interface MappableStore {
  id: number
  label: string
  latitude: number | null
  longitude: number | null
  mapCategory: LogisticsStoreMapCategory
}

/** Point de carte d'une boutique, d'une tournée ou du réseau : même forme, même marqueur. */
export const mapPointOfStore = (store: MappableStore): SimulatorMapPoint | null =>
  mapPointOf('store', store.id, store.label, store.latitude, store.longitude, store.mapCategory)

type MappableKitchen = Omit<MappableStore, 'mapCategory'>
type MappableHub = Omit<MappableKitchen, 'label'> & { name: string }

/** Point de carte d'un atelier, d'une tournée, d'une simulation ou du réseau. */
export const mapPointOfKitchen = (kitchen: MappableKitchen): SimulatorMapPoint | null =>
  mapPointOf('kitchen', kitchen.id, kitchen.label, kitchen.latitude, kitchen.longitude, null)

/** Point de carte d'un hub, d'une tournée, d'une simulation ou du réseau. */
export const mapPointOfHub = (hub: MappableHub): SimulatorMapPoint | null =>
  mapPointOf('hub', hub.id, hub.name, hub.latitude, hub.longitude, null)

/** Point de carte d'un dossier du foncier : celui d'une boutique, à la clé propre au projet. */
export const mapPointOfStoreProject = (project: MappableStore): SimulatorMapPoint | null => {
  const point = mapPointOfStore(project)
  return point === null ? null : { ...point, id: storeProjectPointIdOf(project.id) }
}

// Un projet et une boutique peuvent avoir le même id : clé propre au projet.
const storeProjectPointIdOf = (id: number) => `store_project-${id}`

const STORE_POINT_ID_PATTERN = /^store-(\d+)$/

/**
 * Boutique d'un point de carte (`store-<id>`), celle dont s'ouvre la fiche (#934) ; `null` pour un
 * dossier du foncier, qui n'en a pas, ou tout autre lieu.
 */
export const storeIdOfPointId = (pointId: string): number | null => {
  const match = STORE_POINT_ID_PATTERN.exec(pointId)
  return match === null ? null : Number(match[1])
}

/** Point de carte d'un lieu de tracé (localisé sur le lieu lui-même, supprimé compris). */
export const mapPointOfPathPlace = (place: SimulatorMapPathPlace): SimulatorMapPoint | null => {
  const point = mapPointOf(
    PATH_PLACE_KIND[place.type],
    place.id,
    place.label ?? REMOVED_PLACE_LABEL,
    place.latitude,
    place.longitude,
    place.mapCategory,
  )
  return point !== null && place.type === 'store_project'
    ? { ...point, id: storeProjectPointIdOf(place.id) }
    : point
}

/** Réseau vide, tant qu'il n'est pas lu : référence stable, pour ne pas recalculer les points. */
export const EMPTY_NETWORK: SimulatorNetwork = {
  stores: [],
  storeProjects: [],
  kitchens: [],
  hubs: [],
}

/**
 * Points de carte des tournées visibles (déjà filtrées par statut, `visibleItemsOf`), puis du
 * réseau (`network`, desservi ou non par une tournée) : ateliers et hubs sous leurs cases
 * « Ateliers » et « Hubs », boutiques et dossiers du foncier sous « Boutiques ». Dédupliqués par
 * identité (un même atelier/hub/boutique partagé par plusieurs simulations, ou desservi et présent
 * dans le réseau, n'apparaît qu'une fois, sous le point de la tournée). Un point sans
 * coordonnées connues (atelier/boutique non géolocalisé) est omis plutôt que placé à une position
 * arbitraire.
 */
export const simulatorMapPointsOf = (
  items: SimulatorListItem[],
  display: SimulatorMapDisplayFilters,
  network: SimulatorNetwork = EMPTY_NETWORK,
): SimulatorMapPoint[] => {
  const points = new Map<string, SimulatorMapPoint>()

  const add = (point: SimulatorMapPoint | null) => {
    if (point !== null && !points.has(point.id)) points.set(point.id, point)
  }

  for (const item of items) {
    if (display.showKitchens) add(mapPointOfKitchen(item.kitchen))
    if (display.showHubs) {
      for (const hub of item.hubs) add(mapPointOfHub(hub))
    }
    if (display.showStores) {
      for (const store of item.stores) add(mapPointOfStore(store))
      // Un dossier porté depuis par une boutique se lit par celle-ci, sans second marqueur.
      for (const project of item.storeProjects)
        if (!project.isCarriedByStore) add(mapPointOfStoreProject(project))
    }
    // Lieux du tracé actif : une tournée validée dont le périmètre a changé depuis dessert encore
    // des lieux sortis de `kitchen`/`hubs`/`stores` ; ils gardent leur marqueur (et le cadrage).
    for (const place of item.activeScenario?.paths.flat() ?? []) {
      if (display[DISPLAY_FLAG_OF_KIND[PATH_PLACE_KIND[place.type]]])
        add(mapPointOfPathPlace(place))
    }
  }

  if (display.showKitchens) {
    for (const kitchen of network.kitchens) add(mapPointOfKitchen(kitchen))
  }
  if (display.showHubs) {
    for (const hub of network.hubs) add(mapPointOfHub(hub))
  }
  if (display.showStores) {
    for (const store of network.stores) add(mapPointOfStore(store))
    for (const project of network.storeProjects) add(mapPointOfStoreProject(project))
  }

  return [...points.values()]
}

/**
 * Points de cadrage de la carte : ceux des tournées filtrées (filtres de page et de statut), toutes
 * couches comprises — masquer un type de marqueur (« Affichage ») ne déplace pas la vue. Sans le
 * réseau (boutiques, ateliers, hubs) : la vue reste sur les tournées, pas sur toute la France.
 */
export const simulatorMapFitPointsOf = (items: SimulatorListItem[]): SimulatorMapPoint[] =>
  simulatorMapPointsOf(items, DEFAULT_MAP_DISPLAY_FILTERS)

/**
 * Compteurs des groupes du menu « Filtres » : lieux des tournées filtrées et du réseau,
 * toutes couches comprises — masquer un groupe ne remet pas son compteur à zéro.
 */
export const simulatorMapPointCountsOf = (
  items: SimulatorListItem[],
  network: SimulatorNetwork = EMPTY_NETWORK,
): Record<SimulatorMapPointKind, number> => {
  const counts: Record<SimulatorMapPointKind, number> = { kitchen: 0, hub: 0, store: 0 }
  for (const point of simulatorMapPointsOf(items, DEFAULT_MAP_DISPLAY_FILTERS, network))
    counts[point.kind] += 1
  return counts
}

const isLocated = (coordinate: [number, number] | null): coordinate is [number, number] =>
  coordinate !== null

/** Coordonnées `[lng, lat]` d'un lieu de tracé, `null` s'il n'est pas géolocalisé. */
const pathPlaceCoordinatesOf = (place: SimulatorMapPathPlace): [number, number] | null =>
  place.latitude === null || place.longitude === null ? null : [place.longitude, place.latitude]

/**
 * Coordonnées d'un tracé (un segment) à dessiner, ou `null` s'il ne peut pas l'être : un segment
 * dont un lieu n'est pas géolocalisé est omis en entier plutôt que tracé incomplet. Même règle
 * pour la carte de la liste et celle du détail.
 */
export const locatedPathLine = (
  path: readonly SimulatorMapPathPlace[],
): [number, number][] | null => {
  const coordinates = path.map(pathPlaceCoordinatesOf)
  return coordinates.length >= 2 && coordinates.every(isLocated) ? coordinates : null
}

/**
 * Lignes droites des tracés dont tous les lieux sont géolocalisés (un segment incomplet est omis) :
 * repli des mini-cartes des alternatives quand la route réelle (`scenarioRouteBodiesOf`) n'est pas
 * tracée.
 */
export const locatedPathLinesOf = (
  paths: readonly (readonly SimulatorMapPathPlace[])[],
): [number, number][][] =>
  paths.flatMap((path) => {
    const line = locatedPathLine(path)
    return line === null ? [] : [line]
  })

/**
 * Appels au routeur pour la route réelle d'un scénario actif (carte de la liste et du détail). Un
 * segment n'est tracé que si tous ses lieux sont géolocalisés et que son véhicule est connu
 * (`pathVehicleTypes`, à l'index de `paths`) : sinon il est omis en entier plutôt que tracé
 * incomplet. Sans `pathVehicleTypes` (back-office déployé avant le backend), aucun tracé.
 */
export const scenarioRouteBodiesOf = (scenario: {
  paths: readonly (readonly SimulatorMapPathPlace[])[]
  pathVehicleTypes?: readonly (LogisticsVehicleType | null)[]
}): RouteTraceBody[] =>
  routeTraceBodiesOf(
    scenario.paths.flatMap((path, index) => {
      const coordinates = locatedPathLine(path)
      const vehicleType = scenario.pathVehicleTypes?.[index] ?? null
      return coordinates === null || vehicleType === null ? [] : [{ vehicleType, coordinates }]
    }),
  )

/**
 * Appels au routeur pour les tracés de la carte : ceux du scénario actif de chaque tournée visible
 * (filtrée par statut, `visibleItemsOf`), sous réserve de la case « Tracés ». Une
 * tournée sans scénario actif n'a pas de tracé.
 */
export const simulatorMapRouteBodiesOf = (
  items: SimulatorListItem[],
  showRoutes: boolean,
): RouteTraceBody[] =>
  showRoutes
    ? items.flatMap((item) =>
        item.activeScenario === null ? [] : scenarioRouteBodiesOf(item.activeScenario),
      )
    : []

/** Marges par côté de `fitMapToBounds` (`PaddingOptions` de MapLibre). */
export type MapFitPadding = Exclude<FitMapOptions['padding'], number | undefined>

/**
 * Padding pour `map.fitBounds` : le panneau liste flottant, ancré en haut à gauche de la
 * carte, masque une bande fixe à gauche — sans compensation, l'emprise calculée place des points
 * filtrés sous le panneau. `panelRightEdgePx` = distance en pixels entre le bord gauche de la
 * carte et le bord droit du panneau (décalage + largeur) ; `margin` = marge de respiration
 * appliquée aux quatre côtés, y compris à gauche par-dessus le panneau. Réutilisable par toute
 * carte du simulateur ayant un panneau flottant similaire (ex. carte du détail).
 */
export const simulatorMapFitPadding = (
  panelRightEdgePx: number,
  margin: number,
): MapFitPadding => ({
  top: margin,
  bottom: margin,
  right: margin,
  left: panelRightEdgePx + margin,
})

export const SIMULATOR_STATUS_LABELS: Record<SimulatorScreenStatus, string> = {
  draft: 'Brouillon',
  running: 'Calcul en cours',
  feasible: 'Faisable',
  infeasible: 'Infaisable',
  incomplete: 'Incomplète',
  failed: 'Échec',
  validated: 'Validée',
}

/**
 * Section « Statuts des tournées » du menu « Filtres » de la carte : ses cases, dans leur ordre, et
 * le statut que chaque tournée y présente.
 */
export interface MapStatusFilter<TItem, TStatus extends string> {
  options: readonly { value: TStatus; label: string }[]
  statusOf: (item: TItem) => TStatus
}

/** Filtre de statut de la carte : une option par libellé, dans leur ordre. */
export const mapStatusFilterOf = <TItem, TStatus extends string>(
  labels: Record<TStatus, string>,
  statusOf: (item: TItem) => TStatus,
): MapStatusFilter<TItem, TStatus> => ({
  options: (Object.entries(labels) as [TStatus, string][]).map(([value, label]) => ({
    value,
    label,
  })),
  statusOf,
})

/** Carte du simulateur : statut de la simulation, aux libellés de la liste et de l'export. */
export const SIMULATOR_MAP_STATUS_FILTER = mapStatusFilterOf<
  SimulatorListItem,
  SimulatorScreenStatus
>(SIMULATOR_STATUS_LABELS, (item) => item.status)

/**
 * Lignes de l'export : exactement la liste affichée (filtrée, première page de l'API), avec les
 * mêmes compteurs que les badges.
 */
export const simulatorExportRowsOf = (items: SimulatorListItem[]) =>
  items.map((item) => {
    const badges = badgesOf(item)
    return {
      name: item.name,
      status: SIMULATOR_STATUS_LABELS[item.status],
      vehicleTypes: vehicleTypesLabel(badges.vehicleTypes),
      kitchen: item.kitchen.label,
      hubCount: badges.hubCount,
      storeCount: badges.storeCount,
    }
  })
